import { Box, Button, Chip, MenuItem, Paper, Typography, makeStyles } from '@material-ui/core';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import ChipInput from 'material-ui-chip-input';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { COST_TYPES } from './constants';
import { TOAST_MESSAGE_SEVERITY_ERROR, showToast } from 'modules/layout/layout.actions';
import { TwoDecimalFormat } from 'common/numberFormatCustom.component';
import { mergeClientAndMasterOption } from './versionUtil';
import { useRefCallback } from 'utilities/formUtils';
import AddPhoto from './addPhoto.component';
import ClientBenefitSelectInput from 'common/form/ClientBenefitSelectInput';
import ClientBenefitTextInput from 'common/form/ClientBenefitTextInput';
import Dialog from 'common/dialog.component';
import FullscreenSpinner from 'common/fullscreenSpinner.component';

const useStyles = makeStyles((theme) => (
  {
    root: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 350,
    },
    okButton: {
      marginRight: theme.spacing(2),
      marginLeft: theme.spacing(2),
    },
    cardRoot: {
      width: 300,
    },
  }
));

const BenefitOptionDialog = (props) => {
  const {
    open,
    costType,
    columns,
    isLoading,
    setBenefitOption,
    otherOpts,
    apiCostLookupOptionFields,
    tableCostLookupAuthorizationFields,
    apiCostLookupAuthorizationFields,
  } = props;

  const classes = useStyles();

  const initSelectedOpt = useCallback(() => {
    const selectedOpt = {
      tempId: uuidv4(),
      costVariables: {},
      masterOptionDetail: {
        costVariables: {},
      },
    };
    if (columns) {
      selectedOpt.costVariables = Object.keys(columns).reduce((costVariables, colName) => {
        costVariables[colName] = '';
        return costVariables;
      }, {});
    }
    if (apiCostLookupOptionFields) {
      selectedOpt.costVariables = apiCostLookupOptionFields.reduce((costVariables, field) => {
        costVariables[field.name] = '';
        return costVariables;
      }, {});
    }
    return selectedOpt;
  }, [apiCostLookupOptionFields, columns]);

  const [selectedOpt, setSelectedOpt] = useState(initSelectedOpt());
  const [mergedOpt, setMergedOpt] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);

  const [formRef, setFormRef] = useRefCallback();

  const submit = () => {
    if (otherOpts.some((otherOpt) => otherOpt.name === mergedOpt.name)) {
      props.showToast('Each option must have a unique name.', { severity: TOAST_MESSAGE_SEVERITY_ERROR, userMustDismiss: false });
      return;
    }
    setBenefitOption(selectedOpt);
    close();
  };

  const close = () => {
    setSelectedOpt(initSelectedOpt());
    props.close();
  };

  const renderAuthFields = (authFields) => {
    return (
      <ChipInput
        value={authFields.map((field) => field.label)}
        label="Authorization variables used to calculate the cost:"
        InputProps={{ readOnly: true }}
        chipRenderer={({ chip }) => (
          <Chip
            className="mr"
            key={chip}
            label={chip}
          />
        )}
        disableUnderline
        fullWidth
      />
    );
  };

  useEffect(() => {
    if (props.selectedOpt) {
      setSelectedOpt({ ...props.selectedOpt });
    } else {
      setSelectedOpt(initSelectedOpt());
    }
  }, [props.selectedOpt, initSelectedOpt]);

  useEffect(() => {
    setMergedOpt(mergeClientAndMasterOption(selectedOpt));
  }, [selectedOpt]);

  const validateForm = useCallback(async () => {
    if (!formRef) {
      return;
    }
    let formValid = await formRef.isFormValid();
    if (!isEmpty(mergedOpt.costVariables)) {
      formValid = formValid && Object.values(mergedOpt.costVariables).every((val) => !!val);
    }
    setIsFormValid(formValid);
  }, [formRef, mergedOpt]);

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  return (
    <Dialog
      open={open}
      title={
        <div className="row-no-basis justify-space-between">
          <Typography variant="h5">
            Benefit Option
          </Typography>
        </div>
      }
      content={
        <>
          {isLoading && <FullscreenSpinner />}
          {!isLoading &&
            <div className={classes.root}>
              <AddPhoto
                classes={{ cardRoot: classes.cardRoot }}
                setImageId={(imageId) => setSelectedOpt({ ...selectedOpt, imageId })}
                setImageUrl={(imageUrl) => setSelectedOpt((v) => {v.imageUrl = imageUrl; return v;})}
                imageId={mergedOpt.imageId}
                isReadOnly={false}
                setIsProcessingImage={setIsProcessingImage}
              />
              <ValidatorForm ref={setFormRef} onSubmit={() => {}}>
                <ClientBenefitTextInput
                  clientVersion={selectedOpt}
                  masterVersion={selectedOpt.masterOptionDetail}
                  versionField="name"
                  name="optionName"
                  label="Benefit Option Title"
                  onChange={(value) => setSelectedOpt({ ...selectedOpt, name: value })}
                  onBlur={(value) => setSelectedOpt({ ...selectedOpt, name: value })}
                  required
                  fullWidth
                />
                <ClientBenefitTextInput
                  clientVersion={selectedOpt}
                  masterVersion={selectedOpt.masterOptionDetail}
                  versionField="description"
                  name="optionDescription"
                  label="Description"
                  onChange={(value) => setSelectedOpt({ ...selectedOpt, description: value })}
                  onBlur={(value) => setSelectedOpt({ ...selectedOpt, description: value })}
                  required
                  fullWidth
                />
                {
                  costType === COST_TYPES.AMOUNT ?
                    <ClientBenefitTextInput
                      clientVersion={selectedOpt}
                      masterVersion={selectedOpt.masterOptionDetail}
                      versionField="cost"
                      name="cost"
                      label="Amount"
                      InputProps={{ inputComponent: TwoDecimalFormat }}
                      onChange={(value) => setSelectedOpt({ ...selectedOpt, cost: value })}
                      onBlur={(value) => setSelectedOpt({ ...selectedOpt, cost: value })}
                      required
                      fullWidth
                    /> :
                    <Paper elevation={3}>
                      <Box p={2}>
                        <Typography variant="body1" gutterBottom>Cost Variables: </Typography>
                        {
                          costType === COST_TYPES.TABLE_LOOKUP &&
                            Object.keys(columns).map((columnName) => (
                              <ClientBenefitSelectInput
                                clientVersion={selectedOpt.costVariables}
                                masterVersion={selectedOpt.masterOptionDetail ? selectedOpt.masterOptionDetail.costVariables : undefined}
                                versionField={columnName}
                                key={columnName}
                                name={`costVariables.${columnName}`}
                                label={columns[columnName].label}
                                onChange={(value) => {
                                  setSelectedOpt({
                                    ...selectedOpt,
                                    costVariables: { ...selectedOpt.costVariables, [columnName]: value },
                                  });
                                }}
                                required
                                fullWidth
                              >
                                {columns[columnName].values.map((colVal) => (
                                  <MenuItem
                                    key={colVal}
                                    value={colVal}
                                  >
                                    {colVal}
                                  </MenuItem>
                                ))
                                }
                              </ClientBenefitSelectInput>
                            ))
                        }
                        {costType === COST_TYPES.API_CALC &&
                          apiCostLookupOptionFields.map((field) => (
                            <ClientBenefitTextInput
                              clientVersion={selectedOpt.costVariables}
                              masterVersion={selectedOpt.masterOptionDetail ? selectedOpt.masterOptionDetail.costVariables : undefined}
                              versionField={field.name}
                              key={field.id}
                              name={`costVariables.${field.name}`}
                              label={field.label}
                              onChange={(value) => {
                                setSelectedOpt({
                                  ...selectedOpt,
                                  costVariables: { ...selectedOpt.costVariables, [field.name]: value },
                                });
                              }}
                              onBlur={(value) => {
                                setSelectedOpt({
                                  ...selectedOpt,
                                  costVariables: { ...selectedOpt.costVariables, [field.name]: value },
                                });
                              }}
                              required
                              fullWidth
                            />
                          ))
                        }
                        {renderAuthFields(costType === COST_TYPES.API_CALC ? apiCostLookupAuthorizationFields : tableCostLookupAuthorizationFields)}
                      </Box>
                    </Paper>
                }
              </ValidatorForm>
            </div>
          }
        </>
      }
      actions={
        <div className={classNames('row-no-grow', 'justify-end', 'my')}>
          <Button size="large" onClick={close}>Cancel</Button>
          <Button
            className={classes.okButton}
            size="large"
            variant="contained"
            color="primary"
            onClick={submit}
            disabled={!isFormValid || isProcessingImage}
          >
            Ok
          </Button>
        </div>
      }
    />
  );
};

const mapStateToProps = (state, ownProps) => {
  const isLoading = state.benefit.isLoadingCostLookup;
  let toReturn = { isLoading };
  if (ownProps.costType === COST_TYPES.TABLE_LOOKUP) {
    toReturn = { ...toReturn, ...mapTableLookupProps(state, ownProps) };
  }
  return toReturn;
};

const mapTableLookupProps = (state, ownProps) => {
  if (ownProps.costLookupKey) {
    const costLookupByTable = state.benefit.costLookupByTable[ownProps.costLookupKey];
    if (costLookupByTable) {
      return {
        columns: costLookupByTable.response ? costLookupByTable.response.optionFields : {},
        costList: costLookupByTable.response ? costLookupByTable.response.costList : [],
      };
    }
  }
  return {
    columns: {},
    costList: [],
  };
};

BenefitOptionDialog.propTypes = {
  setBenefitOption: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  costType: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  otherOpts: PropTypes.array.isRequired,
  costLookupKey: PropTypes.string,
  selectedOpt: PropTypes.object,
  columns: PropTypes.object,
  costList: PropTypes.array,
  apiCostLookupOptionFields: PropTypes.array,
  apiCostLookupAuthorizationFields: PropTypes.array,
  tableCostLookupAuthorizationFields: PropTypes.array,

  showToast: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, { showToast })(BenefitOptionDialog);
